import {
  Badge,
  Button,
  cn,
  EmptyState,
  Input,
  Loading,
  Switch,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Map, Search } from 'lucide-react';
import { useMemo, useState } from 'react';

type VisibilityFilter = 'all' | 'enabled' | 'disabled';

const FILTERS: { value: VisibilityFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'enabled', label: 'Enabled' },
  { value: 'disabled', label: 'Disabled' },
];

interface AssignedTour {
  id: string;
  name: string;
  tagline?: string;
  duration?: number;
  stops?: number;
  difficulty?: string;
  tourType?: { id: string; name: string };
  featuredImage?: { url?: string };
  isEnabled: boolean;
}

interface HotelTourToggleListProps {
  hotelId: string;
}

const ASSIGNMENTS_KEY = (hotelId: string) => ['HotelTourAssignments', hotelId];

interface ToggleRowProps {
  tour: AssignedTour;
  onToggle: (tourId: string, isEnabled: boolean) => void;
  pending: boolean;
}

/** A single assigned-tour row with an enable/disable switch. */
function ToggleRow({ tour, onToggle, pending }: ToggleRowProps) {
  return (
    <div
      className={cn(
        'flex items-center gap-4 rounded-lg border bg-card px-4 py-3 shadow-sm transition-opacity',
        !tour.isEnabled && 'opacity-60',
      )}
    >
      {tour.featuredImage?.url ? (
        <img
          src={tour.featuredImage.url}
          alt={tour.name}
          className="h-12 w-16 shrink-0 rounded-md object-cover"
        />
      ) : (
        <div className="flex h-12 w-16 shrink-0 items-center justify-center rounded-md bg-muted">
          <Map className="h-5 w-5 text-muted-foreground" />
        </div>
      )}

      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-zinc-900">{tour.name}</div>
        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          {tour.tourType?.name && <span>{tour.tourType.name}</span>}
          {tour.stops != null && <span>· {tour.stops} stops</span>}
          {tour.duration != null && <span>· {tour.duration} min</span>}
        </div>
      </div>

      <Badge variant={tour.isEnabled ? 'default' : 'secondary'} className="shrink-0">
        {tour.isEnabled ? 'Shown to guests' : 'Hidden'}
      </Badge>

      <Switch
        checked={tour.isEnabled}
        disabled={pending}
        onCheckedChange={(next) => onToggle(tour.id, next)}
        aria-label={`${tour.isEnabled ? 'Disable' : 'Enable'} ${tour.name}`}
      />
    </div>
  );
}

/**
 * Hotel-facing tours view: a hotel can't add or remove tours (those are
 * assigned by Destination Tours) — it can only enable or disable the tours it
 * was given, which controls whether each one is shown to guests in the app.
 * Intentionally has no filters, search, or list chrome — just the toggles.
 */
export function HotelTourToggleList({ hotelId }: HotelTourToggleListProps) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery<{ data: AssignedTour[] }>({
    queryKey: ASSIGNMENTS_KEY(hotelId),
    queryFn: () => apiClient.get<{ data: AssignedTour[] }>(`/hotels/${hotelId}/tour-assignments`),
  });

  const tours = useMemo(() => data?.data ?? [], [data?.data]);

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<VisibilityFilter>('all');

  const visibleTours = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tours.filter((tour) => {
      if (filter === 'enabled' && !tour.isEnabled) return false;
      if (filter === 'disabled' && tour.isEnabled) return false;
      if (q && !tour.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [tours, search, filter]);

  const toggleMutation = useMutation({
    mutationFn: ({ tourId, isEnabled }: { tourId: string; isEnabled: boolean }) =>
      apiClient.patch(`/hotels/${hotelId}/tours/${tourId}`, { isEnabled }),
    // Optimistically flip the switch so it feels instant; roll back on error.
    onMutate: async ({ tourId, isEnabled }) => {
      await queryClient.cancelQueries({ queryKey: ASSIGNMENTS_KEY(hotelId) });
      const previous = queryClient.getQueryData<{ data: AssignedTour[] }>(
        ASSIGNMENTS_KEY(hotelId),
      );
      queryClient.setQueryData<{ data: AssignedTour[] }>(ASSIGNMENTS_KEY(hotelId), (old) =>
        old
          ? { ...old, data: old.data.map((t) => (t.id === tourId ? { ...t, isEnabled } : t)) }
          : old,
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(ASSIGNMENTS_KEY(hotelId), context.previous);
      }
      toast.error('Failed to update tour');
    },
    onSuccess: (_data, { isEnabled }) => {
      toast.success(isEnabled ? 'Tour shown to guests' : 'Tour hidden from guests');
    },
  });

  const handleToggle = (tourId: string, isEnabled: boolean) => {
    toggleMutation.mutate({ tourId, isEnabled });
  };

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-base font-semibold">Your tours</h3>
        <p className="text-sm text-muted-foreground">
          Tours assigned to your hotel. Toggle a tour off to hide it from guests
          in the app; toggle it back on to show it again.
        </p>
      </div>

      {/* Search + visibility filter */}
      {!isLoading && !isError && tours.length > 0 && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative sm:max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tours…"
              className="pl-9"
              aria-label="Search tours"
            />
          </div>
          <div className="inline-flex rounded-md border p-0.5">
            {FILTERS.map((f) => (
              <Button
                key={f.value}
                type="button"
                size="sm"
                variant={filter === f.value ? 'default' : 'ghost'}
                onClick={() => setFilter(f.value)}
              >
                {f.label}
              </Button>
            ))}
          </div>
        </div>
      )}

      {isLoading && <Loading size="sm" />}

      {!isLoading && isError && (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-6 text-center text-sm text-destructive">
          Couldn&apos;t load your tours. Please try again.
        </div>
      )}

      {!isLoading && !isError && tours.length === 0 && (
        <EmptyState
          icon={<Map className="h-6 w-6" />}
          title="No tours assigned yet"
          description="Tours assigned to your hotel by Destination Tours will appear here."
        />
      )}

      {!isLoading && !isError && tours.length > 0 && visibleTours.length === 0 && (
        <EmptyState
          icon={<Search className="h-6 w-6" />}
          title="No matching tours"
          description="No tours match your search or filter."
        />
      )}

      {!isLoading && !isError && visibleTours.length > 0 && (
        <div className="space-y-2">
          {visibleTours.map((tour) => (
            <ToggleRow
              key={tour.id}
              tour={tour}
              onToggle={handleToggle}
              pending={toggleMutation.isPending}
            />
          ))}
        </div>
      )}
    </div>
  );
}
